import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ChevronDown, ChevronUp, MailWarning, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppLink } from '@/components/ui/app-link'
import { useCommunity } from '@/lib/CommunityContext'
import { SUPPORT_EMAIL } from '@/lib/memberEntry'
import { useRoute } from '@/lib/RouteContext'
import { formatSendingDate, setSendingZone } from '@/lib/sendingSuspension'
import { cn } from '@/lib/utils'

const DISMISS_KEY = 'memberfy:sending-alert-dismissed'
const DISMISS_DAYS = 7

/** The number behind the alert: the rate of the window against its limit (`sendingReputation.rates`). */
interface ReputationMetric { kind: 'unsubscribes' | 'complaints'; percent: number; limit: number; windowDays: number }

const dismissedFor = (communityId: string | undefined): boolean => {
  try {
    const raw = localStorage.getItem(`${DISMISS_KEY}:${communityId}`)
    return Boolean(raw && Date.now() - Number(raw) < DISMISS_DAYS * 86400000)
  } catch { return false }
}

/**
 * F-143: the team's notice that Campaigns and Automações are suspended (or,
 * for owner and admin, close to it) because of unsubscribes or spam
 * complaints. The API sends `sendingReputation` only to the team and `alert`
 * only to owner and admin, so a member never sees it. System e-mails
 * (password, login) still go out.
 *
 * UX review 2026-10-26: the suspension is red and stays; the alert is amber
 * and can be dismissed for 7 days. Each says the number against the limit
 * (when the API sends it) and has its action. Outside Campaigns and
 * Workflows it is one line with "Ver detalhes"; the tips show there.
 */
export function SendingReputationBanner({ className }: { className?: string }) {
  const { t, i18n } = useTranslation('common')
  const { community } = useCommunity()
  const { currentPath } = useRoute()
  React.useEffect(() => { setSendingZone(community?.timezone) }, [community?.timezone])
  const [dismissed, setDismissed] = React.useState(() => dismissedFor(community?.id))
  const [expandedHere, setExpandedHere] = React.useState(false)
  React.useEffect(() => { setDismissed(dismissedFor(community?.id)); setExpandedHere(false) }, [community?.id, currentPath])
  const rep = community?.sendingReputation
  if (!rep || (!rep.suspended && !rep.alert)) return null
  const suspended = rep.suspended
  if (!suspended && dismissed) return null
  const where = currentPath || ''
  const full = where.startsWith('/campaigns') || where.startsWith('/workflows')
  const expanded = full || expandedHere
  const reason = rep.reason ? t(`sendingReputation.reasons.${rep.reason}`, { defaultValue: '' }) : ''
  /* The rate that matters (the reason, or the one closer to its limit) against the limit of this state. */
  const metric: ReputationMetric | null = (() => {
    const r = rep.rates
    if (!r) return null
    const limits = suspended ? r.suspend : r.alert
    const ratio = (kind: 'unsubscribes' | 'complaints') => (kind === 'unsubscribes' ? r.unsubscribeRate / (limits.unsubscribeRate || 1) : r.complaintRate / (limits.complaintRate || 1))
    const kind = rep.reason === 'complaints' || rep.reason === 'unsubscribes' ? rep.reason : (ratio('complaints') > ratio('unsubscribes') ? 'complaints' : 'unsubscribes')
    return kind === 'unsubscribes'
      ? { kind, percent: r.unsubscribeRate, limit: limits.unsubscribeRate, windowDays: r.windowDays }
      : { kind, percent: r.complaintRate, limit: limits.complaintRate, windowDays: r.windowDays }
  })()
  const pct = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(n)
  const dismiss = () => {
    try { localStorage.setItem(`${DISMISS_KEY}:${community?.id}`, String(Date.now())) } catch { /* per-browser convenience */ }
    setDismissed(true)
  }
  const title = suspended
    ? (rep.untilLifted || !rep.until ? t('sendingReputation.suspendedUntilLifted') : t('sendingReputation.suspendedUntil', { when: formatSendingDate(rep.until) }))
    : t('sendingReputation.alertTitle')
  return (
    <div role={suspended ? 'alert' : 'status'} data-testid={suspended ? 'sending-suspended-banner' : 'sending-alert-banner'}
      className={cn('border-b px-4 py-3 sm:px-6', suspended ? 'border-red-300 bg-red-50 text-red-950' : 'border-amber-200 bg-amber-50 text-amber-950', className)}>
      <div className="mx-auto flex max-w-7xl gap-3">
        {suspended ? <MailWarning className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" aria-hidden /> : <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden />}
        <div className="min-w-0 flex-1 space-y-1 text-sm">
          <p className="font-semibold">{title}</p>
          {metric && (
            <p data-testid="sending-reputation-metric">{t(`sendingReputation.metric.${metric.kind}`, { value: pct(metric.percent), limit: pct(metric.limit), days: metric.windowDays })}</p>
          )}
          {expanded && (
            <>
              <p>{suspended ? t('sendingReputation.suspendedBody') : t('sendingReputation.alertBody')}{reason ? ` ${t('sendingReputation.reasonLine', { reason })}` : ''}</p>
              {suspended && <p data-testid="sending-tickets-stay">{t('sendingReputation.ticketsStay')}</p>}
              {rep.tips && rep.tips.length > 0 && (
                <ul className="list-disc space-y-0.5 pl-5" data-testid="sending-reputation-tips">
                  {rep.tips.map((tip) => <li key={tip}>{t(`sendingReputation.tips.${tip}`, { defaultValue: tip })}</li>)}
                </ul>
              )}
            </>
          )}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button variant="outline" size="sm" className="min-h-9 bg-white" asChild>
              <AppLink to="/campaigns?tab=opt-outs" data-testid="sending-reputation-optouts">{t('sendingReputation.seeOptOuts')}</AppLink>
            </Button>
            {suspended && (
              <Button variant="outline" size="sm" className="min-h-9 bg-white" asChild>
                <a href={`mailto:${SUPPORT_EMAIL}`}>{t('sendingReputation.contact')}</a>
              </Button>
            )}
            {!full && (
              <button type="button" className="inline-flex min-h-9 items-center gap-1 px-1 text-sm font-medium underline-offset-2 hover:underline" aria-expanded={expandedHere}
                onClick={() => setExpandedHere((v) => !v)} data-testid="sending-reputation-details">
                {expandedHere ? t('sendingReputation.lessDetails') : t('sendingReputation.moreDetails')}
                {expandedHere ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </button>
            )}
          </div>
        </div>
        {!suspended && (
          <button type="button" onClick={dismiss} className="h-9 w-9 flex-shrink-0 rounded-md p-2 text-amber-800 hover:bg-amber-100" aria-label={t('sendingReputation.dismiss')} title={t('sendingReputation.dismiss')}>
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
    </div>
  )
}
